import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LangContext";

const Nav = () => {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top thin banner */}
      <div
        data-testid="top-banner"
        className="w-full text-center text-[11px] tracking-[0.28em] uppercase py-2 text-po-accent bg-po-accent/[0.06] border-b border-po-accent/10"
      >
        {t.nav.banner}
      </div>

      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-2xl bg-[#050A07]/70 border-b border-white/[0.04]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[68px] flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="logo-button"
            className="flex items-center gap-2 group"
          >
            <div className="relative h-7 w-7 rounded-md bg-gradient-to-br from-po-accent to-po-accent2 flex items-center justify-center">
              <div className="absolute inset-0 rounded-md bg-po-accent/30 blur-md group-hover:blur-lg transition" />
              <span className="relative text-[#04140C] font-display font-bold text-sm">P</span>
            </div>
            <span className="font-display font-semibold text-[15px] tracking-tight text-white">
              ProfitOperators
            </span>
          </button>

          {/* Center links */}
          <div className="hidden md:flex items-center gap-8 text-[13px] text-po-text2">
            {[
              { id: "how", label: t.nav.howItWorks },
              { id: "benefits", label: t.nav.benefits },
              { id: "products", label: t.nav.products },
              { id: "faq", label: t.nav.faq },
            ].map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="hover:text-white transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <button
              onClick={toggle}
              data-testid="lang-toggle"
              className="text-[11px] tracking-[0.22em] uppercase px-3 py-1.5 rounded-full border border-white/10 text-po-text2 hover:text-white hover:border-po-accent/40 transition flex items-center gap-1.5"
            >
              <span className={lang === "en" ? "text-white" : ""}>EN</span>
              <span className="text-white/20">/</span>
              <span className={lang === "fr" ? "text-white" : ""}>FR</span>
            </button>
            <button
              onClick={() => scrollTo("contact")}
              data-testid="nav-cta-start-earning"
              className="btn-glow group hidden sm:inline-flex items-center gap-1.5 bg-po-accent text-[#04140C] font-semibold text-[13px] rounded-full px-4 py-2"
            >
              {t.nav.cta}
              <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
            </button>
          </div>
        </div>
      </motion.nav>
    </header>
  );
};

export default Nav;
